import { STUDIO_CAPACITY } from "./capacityConfig.js";

const dateKey = (date) => date.toISOString().slice(0, 10);
const overlaps = (aStart, aEnd, bStart, bEnd) =>
  aStart && aEnd && bStart && bEnd && aStart <= bEnd && bStart <= aEnd;

export function evaluateKilnSchedule({ kilnEvents = [], capacity = STUDIO_CAPACITY, now = new Date() } = {}) {
  const byDate = {};
  const byDateKiln = {};
  const utilizationByDay = {};
  const conflicts = [];
  const underutilizedFirings = [];
  const gaps = [];

  const volume = capacity.kilnVolumeLitres || STUDIO_CAPACITY.kilnVolumeLitres;
  const factor = capacity.kilnLoadFactor || STUDIO_CAPACITY.kilnLoadFactor || 1;
  const maxLoad = volume * factor;

  // Group events by date+kinn
  kilnEvents.forEach((ev) => {
    if (!ev.start && ev.end) ev.start = ev.end;
    if (!ev.end && ev.start) ev.end = ev.start;
    if (!ev.start || !ev.end) return;
    const dk = dateKey(ev.start);
    const kid = ev.kilnId || "default";
    byDate[dk] = byDate[dk] || [];
    byDate[dk].push(ev);
    byDateKiln[dk] = byDateKiln[dk] || {};
    byDateKiln[dk][kid] = byDateKiln[dk][kid] || [];
    byDateKiln[dk][kid].push(ev);
  });

  // Utilization + conflicts per date+kinn
  Object.entries(byDateKiln).forEach(([dk, kilnMap]) => {
    utilizationByDay[dk] = utilizationByDay[dk] || {};
    Object.entries(kilnMap).forEach(([kid, events]) => {
      const totalLoad = events.reduce((sum, ev) => sum + (Number(ev.estimatedLoadLitres) || 0), 0);
      const util = maxLoad > 0 ? totalLoad / maxLoad : 0;
      utilizationByDay[dk][kid] = util;
      if (util > 1) {
        conflicts.push({
          date: dk,
          kilnId: kid,
          eventIds: events.map((e) => e.id),
          reason: "over_capacity",
          utilization: util,
        });
      }
      // time overlaps
      for (let i = 0; i < events.length; i++) {
        for (let j = i + 1; j < events.length; j++) {
          if (overlaps(events[i].start, events[i].end, events[j].start, events[j].end)) {
            conflicts.push({
              date: dk,
              kilnId: kid,
              eventIds: [events[i].id, events[j].id],
              reason: "time_overlap",
              utilization: util,
            });
          }
        }
      }
      // underutilized firings
      events.forEach((ev) => {
        const evLoad = Number(ev.estimatedLoadLitres) || 0;
        const evUtil = maxLoad > 0 ? evLoad / maxLoad : 0;
        if (evUtil > 0 && evUtil < 0.4) {
          underutilizedFirings.push({
            firingId: ev.id,
            date: dk,
            kilnId: kid,
            utilization: evUtil,
          });
        }
      });
    });
  });

  // Gaps per kiln (simple detection)
  const eventsByKiln = {};
  kilnEvents.forEach((ev) => {
    const kid = ev.kilnId || "default";
    eventsByKiln[kid] = eventsByKiln[kid] || [];
    if (ev.start && ev.end) eventsByKiln[kid].push(ev);
  });

  Object.entries(eventsByKiln).forEach(([kid, list]) => {
    const sorted = [...list].sort((a, b) => a.start - b.start);
    for (let i = 0; i < sorted.length - 1; i++) {
      const gapStart = sorted[i].end;
      const gapEnd = sorted[i + 1].start;
      if (gapStart && gapEnd) {
        const durationHours = (gapEnd - gapStart) / (1000 * 60 * 60);
        if (durationHours >= 6) {
          gaps.push({
            kilnId: kid,
            start: gapStart,
            end: gapEnd,
            durationHours,
          });
        }
      }
    }
  });

  return {
    byDate,
    utilizationByDay,
    conflicts,
    underutilizedFirings,
    gaps,
  };
}
